import { normalizeDriverApplication } from './driver-applications';

export function normalizeReferralCode(value) {
  return String(value || '')
    .trim()
    .toUpperCase();
}

/**
 * @param {ReturnType<typeof normalizeDriverApplication>[]} drivers
 */
export function buildReferralReport(drivers) {
  const list = (drivers || []).map(normalizeDriverApplication);

  const codeToReferrer = new Map();
  list.forEach((driver) => {
    const code = normalizeReferralCode(driver.referral_code);
    if (code && !codeToReferrer.has(code)) {
      codeToReferrer.set(code, driver);
    }
  });

  const referredDrivers = list.filter((d) => normalizeReferralCode(d.referred_by));

  const byCode = new Map();

  const ensureEntry = (code) => {
    if (!byCode.has(code)) {
      const referrer = codeToReferrer.get(code) || null;
      byCode.set(code, {
        referralCode: code,
        referrer,
        referrals: [],
      });
    }
    return byCode.get(code);
  };

  referredDrivers.forEach((referred) => {
    const code = normalizeReferralCode(referred.referred_by);
    ensureEntry(code).referrals.push(referred);
  });

  const referrerSummaries = Array.from(byCode.values())
    .map((entry) => {
      const approved = entry.referrals.filter((r) => r.status === 'approved').length;
      const pending = entry.referrals.filter((r) =>
        ['pending', 'reviewing'].includes(r.status)
      ).length;
      return {
        referralCode: entry.referralCode,
        referrer: entry.referrer,
        referrerId: entry.referrer?.id || null,
        referrerName: entry.referrer?.full_name || '',
        referrerPhone: entry.referrer?.phone || '',
        referrerStatus: entry.referrer?.status || '',
        referralCount: entry.referrals.length,
        approvedCount: approved,
        pendingCount: pending,
        isUnknownReferrer: !entry.referrer,
        referrals: entry.referrals.sort(
          (a, b) => Date.parse(b.created_at || 0) - Date.parse(a.created_at || 0)
        ),
      };
    })
    .filter((row) => row.referralCount > 0 && !row.isUnknownReferrer)
    .sort(
      (a, b) =>
        b.referralCount - a.referralCount ||
        a.referrerName.localeCompare(b.referrerName, undefined, { sensitivity: 'base' })
    );

  const referralEvents = referredDrivers
    .map((referred) => {
      const code = normalizeReferralCode(referred.referred_by);
      const referrer = codeToReferrer.get(code) || null;
      return {
        referralCode: code,
        referred,
        referrer,
        referrerName: referrer?.full_name || '',
        referrerId: referrer?.id || null,
        isUnknownReferrer: !referrer,
      };
    })
    .filter((event) => !event.isUnknownReferrer)
    .sort(
      (a, b) =>
        Date.parse(b.referred.created_at || 0) - Date.parse(a.referred.created_at || 0)
    );

  return {
    totalReferrals: referralEvents.length,
    referrersWithReferrals: referrerSummaries.length,
    referrerSummaries,
    referralEvents,
  };
}

export async function fetchReferralReport(supabaseClient) {
  if (!supabaseClient) {
    return { report: null, error: 'Supabase is not configured.' };
  }

  const { data, error } = await supabaseClient
    .from('drivers')
    .select(
      'id, full_name, phone, status, referral_code, referred_by, created_at, signup_source, city, state'
    )
    .order('created_at', { ascending: false });

  if (error) {
    return {
      report: null,
      error: error.message || 'Could not load referral data.',
    };
  }

  return {
    report: buildReferralReport(data || []),
    error: '',
  };
}
